import React, { useState } from 'react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import * as Tooltip from '@radix-ui/react-tooltip';
import {
  ConsumableDefinition,
  JokerDefinition,
  RunShopState,
  describeJokerEffect,
  describeConsumableEffect,
} from '@solante/shared';
import { soundManager } from '../../../utils/sounds';

interface PlayerRunSnapshot {
  chips: number;
  hearts: number;
  totalScore: number;
  jokerSlots: number;
  jokers: JokerDefinition[];
  consumables: ConsumableDefinition[];
  consumablesUsed: string[];
  pendingScorePercent: number;
  tempoStreak: number;
  phase: 'play' | 'shop';
  nextShopTurn: number;
  shopGeneration: number;
  shopRerolls: number;
}

interface CompactRunPanelProps {
  runState?: PlayerRunSnapshot;
  shopState?: RunShopState;
  phase?: 'play' | 'shop';
  onBuy?: (offerId: string) => Promise<void>;
  onReroll?: () => Promise<void>;
  onUseConsumable?: (consumableId: string) => Promise<void>;
  onReady?: () => Promise<void>;
}

import { RARITY_COLORS } from '../../../utils/gameConstants';

export const CompactRunPanel: React.FC<CompactRunPanelProps> = ({
  runState,
  shopState,
  phase = 'play',
  onBuy,
  onReroll,
  onUseConsumable,
  onReady,
}) => {
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [expandedSection, setExpandedSection] = useState<'jokers' | 'consumables' | 'shop' | null>(
    phase === 'shop' ? 'shop' : null,
  );

  const effectivePhase = runState?.phase ?? phase;
  const isShopOpen = effectivePhase === 'shop' && !!shopState;
  const jokers = runState?.jokers ?? [];
  const consumables = runState?.consumables ?? [];
  const shopOffers = shopState?.offers ?? [];
  const rerollCost = shopState?.rerollCost ?? 0;

  const handleBuy = async (offerId: string) => {
    if (!onBuy || pendingAction || !isShopOpen) return;
    try {
      setPendingAction(`buy-${offerId}`);
      await onBuy(offerId);
      soundManager.play('coin');
      toast.success('Purchased!');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Purchase failed');
      soundManager.play('error');
    } finally {
      setPendingAction(null);
    }
  };

  const handleReroll = async () => {
    if (!onReroll || pendingAction || !isShopOpen) return;
    try {
      setPendingAction('reroll');
      await onReroll();
      soundManager.play('select');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Reroll failed');
      soundManager.play('error');
    } finally {
      setPendingAction(null);
    }
  };

  const handleUseConsumable = async (consumableId: string) => {
    if (!onUseConsumable || pendingAction) return;
    try {
      setPendingAction(`consume-${consumableId}`);
      await onUseConsumable(consumableId);
      soundManager.play('powerup');
      toast.success('Activated!');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to use');
      soundManager.play('error');
    } finally {
      setPendingAction(null);
    }
  };

  const handleReady = async () => {
    if (!onReady || !isShopOpen) return;
    try {
      await onReady();
      soundManager.play('select');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Ready failed');
    }
  };

  const toggleSection = (section: 'jokers' | 'consumables' | 'shop') => {
    setExpandedSection(expandedSection === section ? null : section);
    soundManager.play('click');
  };

  if (!runState) {
    return <div className="arcade-panel p-4 text-sm text-white/70">Syncing loadout...</div>;
  }

  return (
    <Tooltip.Provider>
      <div className="arcade-panel p-4 space-y-3">
        <h3 className="text-xs uppercase tracking-widest text-neon-500">Loadout</h3>

        {/* Jokers Section */}
        <div className="border-b border-white/10 pb-2">
          <button
            type="button"
            onClick={() => toggleSection('jokers')}
            className="flex w-full items-center justify-between text-xs uppercase tracking-wide text-white/70 hover:text-white"
          >
            <span>Jokers ({jokers.length})</span>
            <span>{expandedSection === 'jokers' ? '▲' : '▼'}</span>
          </button>
          <AnimatePresence>
            {expandedSection === 'jokers' && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                {jokers.length === 0 ? (
                  <p className="mt-2 text-xs text-white/40">No jokers yet</p>
                ) : (
                  <ul className="mt-2 space-y-1">
                    {jokers.map((joker) => (
                      <Tooltip.Root key={joker.id} delayDuration={100}>
                        <Tooltip.Trigger asChild>
                          <li
                            className={`cursor-help rounded-lg border px-2 py-1 text-xs ${RARITY_COLORS[joker.rarity] ?? RARITY_COLORS['common']}`}
                          >
                            <span className="font-semibold text-white">{joker.name}</span>
                          </li>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Content className="arcade-tooltip" sideOffset={5}>
                            <p className="font-semibold text-white">{joker.name}</p>
                            <p className="text-xs uppercase text-white/40">{joker.rarity}</p>
                            {joker.effects.length > 0 && (
                              <ul className="mt-1 space-y-0.5 border-t border-white/10 pt-1">
                                {joker.effects.map((effect, i) => (
                                  <li key={i} className="text-xs text-arcade-cyan">
                                    + {describeJokerEffect(effect)}
                                  </li>
                                ))}
                              </ul>
                            )}
                            <Tooltip.Arrow className="fill-black/95" />
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    ))}
                  </ul>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Consumables Section */}
        <div className="border-b border-white/10 pb-2">
          <button
            type="button"
            onClick={() => toggleSection('consumables')}
            className="flex w-full items-center justify-between text-xs uppercase tracking-wide text-white/70 hover:text-white"
          >
            <span>Consumables ({consumables.length})</span>
            <span>{expandedSection === 'consumables' ? '▲' : '▼'}</span>
          </button>
          <AnimatePresence>
            {expandedSection === 'consumables' && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                {consumables.length === 0 ? (
                  <p className="mt-2 text-xs text-white/40">No consumables</p>
                ) : (
                  <ul className="mt-2 space-y-1">
                    {consumables.map((item) => (
                      <Tooltip.Root key={item.id} delayDuration={100}>
                        <Tooltip.Trigger asChild>
                          <li className="flex cursor-help items-center justify-between rounded-lg border border-white/10 bg-white/5 px-2 py-1">
                            <div className="flex-1 min-w-0">
                              <span className="text-xs font-semibold text-white truncate block">
                                {item.name}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUseConsumable(item.id);
                              }}
                              disabled={pendingAction !== null}
                              className="ml-2 rounded border border-emerald-400/60 px-2 py-0.5 text-xs text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-50"
                            >
                              USE
                            </button>
                          </li>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Content
                            className="arcade-tooltip border-emerald-400/30"
                            sideOffset={5}
                          >
                            <p className="font-semibold text-white">{item.name}</p>
                            <p className="text-xs text-white/60">{item.description}</p>
                            {item.effects.length > 0 && (
                              <ul className="mt-1 space-y-0.5 border-t border-white/10 pt-1">
                                {item.effects.map((effect, i) => (
                                  <li key={i} className="text-xs text-emerald-300">
                                    + {describeConsumableEffect(effect)}
                                  </li>
                                ))}
                              </ul>
                            )}
                            <Tooltip.Arrow className="fill-black/95" />
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    ))}
                  </ul>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Shop Section */}
        <div>
          <button
            type="button"
            onClick={() => toggleSection('shop')}
            className={`flex w-full items-center justify-between text-xs uppercase tracking-wide ${
              isShopOpen ? 'text-emerald-400' : 'text-white/70'
            } hover:text-white`}
          >
            <span>Shop {isShopOpen && '(OPEN)'}</span>
            <span>{expandedSection === 'shop' ? '▲' : '▼'}</span>
          </button>
          <AnimatePresence>
            {expandedSection === 'shop' && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                {!isShopOpen ? (
                  <p className="mt-2 text-xs text-white/40">Shop closed — keep playing to unlock</p>
                ) : (
                  <div className="mt-2 space-y-2">
                    {shopOffers.map((offer) => {
                      const item =
                        offer.kind === 'joker'
                          ? offer.joker
                          : offer.kind === 'consumable'
                            ? offer.consumable
                            : offer.service;
                      const cost = item?.cost ?? 0;
                      const name = item?.name ?? 'Unknown';
                      const isBuying = pendingAction === `buy-${offer.offerId}`;

                      // Get description and rarity
                      const description =
                        offer.kind === 'joker'
                          ? offer.joker?.description
                          : offer.kind === 'consumable'
                            ? offer.consumable?.description
                            : offer.service?.description;
                      const rarity = offer.kind === 'joker' ? offer.joker?.rarity : undefined;

                      // Render effects list based on kind
                      const renderEffects = () => {
                        if (offer.kind === 'joker' && offer.joker?.effects.length) {
                          return (
                            <ul className="mt-1.5 space-y-0.5 border-t border-white/10 pt-1.5">
                              {offer.joker.effects.map((effect, i) => (
                                <li key={i} className="text-xs text-arcade-cyan">
                                  + {describeJokerEffect(effect)}
                                </li>
                              ))}
                            </ul>
                          );
                        }
                        if (offer.kind === 'consumable' && offer.consumable?.effects.length) {
                          return (
                            <ul className="mt-1.5 space-y-0.5 border-t border-white/10 pt-1.5">
                              {offer.consumable.effects.map((effect, i) => (
                                <li key={i} className="text-xs text-emerald-300">
                                  + {describeConsumableEffect(effect)}
                                </li>
                              ))}
                            </ul>
                          );
                        }
                        return null;
                      };

                      return (
                        <Tooltip.Root key={offer.offerId} delayDuration={100}>
                          <Tooltip.Trigger asChild>
                            <div className="flex cursor-help items-center justify-between rounded-lg border border-white/10 bg-white/5 px-2 py-1">
                              <div className="flex-1 min-w-0">
                                <span className="text-xs font-semibold text-white truncate block">
                                  <span
                                    className={`text-[10px] font-bold mr-1 px-1 rounded ${
                                      offer.kind === 'joker'
                                        ? 'bg-blue-500/30 text-blue-300'
                                        : offer.kind === 'consumable'
                                          ? 'bg-emerald-500/30 text-emerald-300'
                                          : 'bg-purple-500/30 text-purple-300'
                                    }`}
                                  >
                                    {offer.kind === 'joker'
                                      ? 'J'
                                      : offer.kind === 'consumable'
                                        ? 'C'
                                        : 'S'}
                                  </span>
                                  {name}
                                </span>
                                <span className="text-xs text-amber-300">{cost}⛁</span>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBuy(offer.offerId);
                                }}
                                disabled={isBuying || pendingAction !== null}
                                className="ml-2 rounded border border-neon-400/60 px-2 py-0.5 text-xs text-neon-200 hover:bg-neon-500/20 disabled:opacity-50"
                              >
                                {isBuying ? '...' : 'BUY'}
                              </button>
                            </div>
                          </Tooltip.Trigger>
                          <Tooltip.Portal>
                            <Tooltip.Content
                              className={`arcade-tooltip ${
                                offer.kind === 'joker'
                                  ? 'border-blue-400/30'
                                  : offer.kind === 'consumable'
                                    ? 'border-emerald-400/30'
                                    : ''
                              }`}
                              sideOffset={5}
                              side="top"
                            >
                              <p className="font-semibold text-white">{name}</p>
                              {rarity && (
                                <p className="text-xs uppercase text-white/40">{rarity}</p>
                              )}
                              {description && <p className="mt-1 text-white/60">{description}</p>}
                              {renderEffects()}
                              <Tooltip.Arrow className="fill-black/95" />
                            </Tooltip.Content>
                          </Tooltip.Portal>
                        </Tooltip.Root>
                      );
                    })}

                    <div className="flex gap-2 mt-3">
                      <button
                        type="button"
                        onClick={handleReroll}
                        disabled={pendingAction === 'reroll' || pendingAction !== null}
                        className="flex-1 rounded border border-white/20 px-2 py-1 text-xs text-white/70 hover:bg-white/10 disabled:opacity-50"
                      >
                        🔄 Reroll ({rerollCost}⛁)
                      </button>
                      <button
                        type="button"
                        onClick={handleReady}
                        className="flex-1 rounded border border-emerald-400/60 px-2 py-1 text-xs text-emerald-200 hover:bg-emerald-500/20"
                      >
                        ✓ READY
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </Tooltip.Provider>
  );
};
